import { ProfileData } from '@/types/profile';
import { ProfileFilters } from '@/hooks/useProfileFilters';
import { debugLogger } from '@/utils/debugLogger';
export type SortOption = "recent" | "distance" | "online" | "verified" | "new" | "bestMatch";

export const sortOptions = [
  { label: 'Mais relevantes', value: 'distance' },
  { label: 'Mais recentes', value: 'recent' },
  { label: 'Online agora', value: 'online' },
  { label: 'Verificados', value: 'verified' }
];

export const sortProfiles = (
  profiles: ProfileData[], 
  sortBy: SortOption, 
  userLatitude?: number, 
  userLongitude?: number
): ProfileData[] => {
  if (!profiles || profiles.length === 0) return profiles;

  const sortedProfiles = [...profiles];
  
  switch (sortBy) {
    case 'recent':
      return sortedProfiles.sort((a, b) => {
        // 1. Primeiro: usuários online aparecem primeiro
        if (a.is_online && !b.is_online) return -1;
        if (!a.is_online && b.is_online) return 1;
        
        // 2. Se ambos têm o mesmo status online, ordenar por last_seen
        const aLastSeen = a.last_seen ? new Date(a.last_seen).getTime() : 0;
        const bLastSeen = b.last_seen ? new Date(b.last_seen).getTime() : 0;
        
        // Se ambos têm last_seen, ordenar pelo mais recente
        if (aLastSeen && bLastSeen) {
          return bLastSeen - aLastSeen;
        }
        
        // Se apenas um tem last_seen, ele vem primeiro
        if (aLastSeen && !bLastSeen) return -1;
        if (!aLastSeen && bLastSeen) return 1;
        
        // 3. Se nenhum tem last_seen, ordenar por created_at ou last_login
        const aFallback = new Date(a.last_login || a.created_at || 0).getTime();
        const bFallback = new Date(b.last_login || b.created_at || 0).getTime();
        
        return bFallback - aFallback;
      });
    
    case 'distance':
      // Usar a distância já calculada pelo backend (distance_km)
      // O backend calcula via get_feed_data e retorna no campo distance
      return sortedProfiles.sort((a, b) => {
        const aDistance = a.distance ?? 999999;
        const bDistance = b.distance ?? 999999;
        return aDistance - bDistance;
      });
    
    case 'online':
      // FILTRAR apenas perfis online
      const onlineProfiles = sortedProfiles.filter(profile => profile.is_online === true);
      
      // ORDENAR por last_seen (mais recente primeiro)
      return onlineProfiles.sort((a, b) => {
        return new Date(b.last_seen || 0).getTime() - new Date(a.last_seen || 0).getTime();
      });
    
    case 'verified':
      // FILTRAR apenas perfis com verificação completa
      const verifiedProfiles = sortedProfiles.filter(
        profile => profile.verification_level === 'full'
      );
      
      // ORDENAR por data de criação (mais recente primeiro)
      return verifiedProfiles.sort((a, b) => {
        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
      });
    
    case 'new':
      return sortedProfiles.sort((a, b) => {
        // Ordenar EXCLUSIVAMENTE por created_at (mais novo primeiro)
        const aCreatedAt = new Date(a.created_at || 0).getTime();
        const bCreatedAt = new Date(b.created_at || 0).getTime();
        return bCreatedAt - aCreatedAt;
      });
    
    case 'bestMatch':
    default:
      return sortedProfiles;
  }
};

export const filterProfiles = (
  profiles: ProfileData[], 
  userRole?: string, 
  userOrientation?: string
): ProfileData[] => {
  if (!profiles || profiles.length === 0) return profiles;
  
  return profiles.filter(profile => {
    // Filter based on role compatibility
    if (userRole === 'sponsor' && profile.role !== 'sponsored') return false;
    if (userRole === 'sponsored' && profile.role !== 'sponsor') return false;
    
    // Filter based on orientation compatibility
    if (userOrientation && profile.orientation) {
      // Basic orientation filtering logic
      if (userOrientation === 'heterosexual' && profile.gender === profile.orientation) return false;
      if (userOrientation === 'homosexual' && profile.gender !== profile.orientation) return false;
    }
    
    // Filter out hidden profiles
    if (profile.hidden) return false;
    
    // Filter out incomplete profiles
    if (!profile.onboarding_completed) return false;
    
    return true;
  });
};

export const applyAdvancedFilters = (
  profiles: ProfileData[],
  filters: ProfileFilters
): ProfileData[] => {
  if (!profiles || profiles.length === 0) return profiles;

  return profiles.filter(profile => {
    // Gender filter
    if (filters.gender && filters.gender.length > 0) {
      if (!filters.gender.includes(profile.gender || '')) return false;
    }

    // Orientation filter
    if (filters.orientation && filters.orientation.length > 0) {
      if (!filters.orientation.includes(profile.orientation || '')) return false;
    }

    // Role filter
    if (filters.role && filters.role.length > 0) {
      if (!filters.role.includes(profile.role || '')) return false;
    }

    // Age range filter using separate min/max values
    if (filters.minAge !== undefined && profile.age < filters.minAge) return false;
    if (filters.maxAge !== undefined && profile.age > filters.maxAge) return false;

    // Cities filter
    if (filters.cities && filters.cities.length > 0) {
      if (!filters.cities.includes(profile.city || '')) return false;
    }

    // States filter
    if (filters.states && filters.states.length > 0) {
      if (!filters.states.includes(profile.state_code || '')) return false;
    }

    // Relationship types filter
    if (filters.relationshipTypes && filters.relationshipTypes.length > 0) {
      if (!filters.relationshipTypes.includes(profile.relationship_type || '')) return false;
    }

    // Marital status filter (using English field)
    if (filters.estadoCivil && filters.estadoCivil.length > 0) {
      if (!filters.estadoCivil.includes(profile.marital_status || '')) return false;
    }

    // Physical characteristics filters (using English fields)
    if (filters.alturas && filters.alturas.length > 0) {
      debugLogger.debug('HeightFilter', 'Checking profile:', {
        username: profile.username,
        profileHeight: profile.height,
        filterValues: filters.alturas,
        willPass: filters.alturas.includes(profile.height || '')
      });
      
      if (!filters.alturas.includes(profile.height || '')) {
        debugLogger.debug('HeightFilter', 'Filtered out:', { username: profile.username });
        return false;
      }
      debugLogger.debug('HeightFilter', 'Passed:', { username: profile.username });
    }

    if (filters.tiposCorpo && filters.tiposCorpo.length > 0) {
      if (!filters.tiposCorpo.includes(profile.body_type || '')) return false;
    }

    if (filters.coresOlhos && filters.coresOlhos.length > 0) {
      if (!filters.coresOlhos.includes(profile.eye_color || '')) return false;
    }

    if (filters.coresCabelo && filters.coresCabelo.length > 0) {
      if (!filters.coresCabelo.includes(profile.hair_color || '')) return false;
    }

    // Lifestyle filters (using English fields)
    if (filters.fumante && filters.fumante.length > 0) {
      if (!filters.fumante.includes(profile.smoker || '')) return false;
    }

    if (filters.bebidas && filters.bebidas.length > 0) {
      if (!filters.bebidas.includes(profile.drinker || '')) return false;
    }

    if (filters.tatuagens && filters.tatuagens.length > 0) {
      if (!filters.tatuagens.includes(profile.tattoos || '')) return false;
    }

    if (filters.piercings && filters.piercings.length > 0) {
      if (!filters.piercings.includes(profile.piercings || '')) return false;
    }

    // Verification level filter
    if (filters.verificationLevels && filters.verificationLevels.length > 0) {
      if (!filters.verificationLevels.includes(profile.verification_level || '')) return false;
    }

    // Interests filter - changed to require ALL selected interests
    if (filters.interests && filters.interests.length > 0) {
      const profileInterests = profile.interests || [];
      // Profile must have ALL selected interests (changed from some to every)
      const hasAllInterests = filters.interests.every(interest => 
        profileInterests.includes(interest)
      );
      if (!hasAllInterests) return false;
    }

    return true;
  });
};

export const formatLocation = (city: string | null, stateCode: string | null): string => {
  if (city && stateCode) {
    return `${city}, ${stateCode}`;
  }
  return city || stateCode || 'Localização não informada';
};
